import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';

const SignUpLayer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    mobile: '',
    businessCard: '', // Will hold the Base64 string
    developer_id: '',
    type: 'developerSales',
  });

  // Changed error initial state to an array for multiple error messages
  const [error, setError] = useState([]);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpTimer, setOtpTimer] = useState(60); // Timer in seconds
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [developers, setDevelopers] = useState([]);

  // Transform developers data for react-select
  const developerOptions = useMemo(() => {
    return developers.map(dev => ({
      value: dev.id,
      label: dev.name
    }));
  }, [developers]);

  // Handle select change
  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      developer_id: selectedOption ? selectedOption.value : ''
    }));
  };

  // Field Validations
  const validateFields = () => {
    const errors = [];
    // Business Email Validation: must be a business email (not gmail, yahoo, outlook, hotmail)
    if (formData.email) {
      const emailParts = formData.email.split('@');
      if (emailParts.length !== 2) {
        errors.push('Invalid email format.');
      } else {
        const domain = emailParts[1].toLowerCase();
        const disallowedDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
        if (disallowedDomains.includes(domain)) {
          errors.push('Please use a business email address.');
        }
      }
    } else {
      errors.push('Email is required.');
    }
    // Password Validation using regex
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      errors.push('Password must contain at least one uppercase letter, one number, one special character, and minimum 8 characters (@$!%*?&).');
    }
    // Password Confirmation
    if (formData.password !== formData.password_confirmation) {
      errors.push('Passwords do not match.');
    }
    // Mobile number should be exactly 11 characters
    if (formData.mobile.length !== 11) {
      errors.push('Mobile number must be 11 digits.');
    }
    // Business Card Validation: should be a valid image (Base64 string starting with "data:image/")
    if (!formData.businessCard || !formData.businessCard.startsWith('data:image/')) {
      errors.push('Please upload a valid business card image.');
    }
    return errors;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else if (field === 'password_confirmation') {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Handle file input and convert to Base64
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      setError(['File size exceeds the maximum limit of 2MB.']);
    } else if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, businessCard: reader.result }); // Save as Base64
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    fetchDevelopers();
  }, []);

  const fetchDevelopers = async () => {
    try {
      const endpoint = '/developers/approvedOnly';
      const { data } = await api.get(endpoint);
      if (data.status) {
        setDevelopers(data.response.data);
      } else {
        console.error('Error fetching developers:', data.message);
      }
    } catch (error) {
      console.error('Error fetching developers:', error);
    }
  };

  // Handle Signup Submission
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError([]); // Clear previous errors

    // Run validations
    const validationErrors = validateFields();
    if (validationErrors.length) {
      setError(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Instead of registering immediately, send OTP to email
      await api.post('/otp/sendOtp', { email: formData.email });
      setOtpTimer(60); // Reset OTP timer
      setShowOtpModal(true); // Show OTP modal
    } catch (err) {
      const errorResponse = err.response?.data?.response;
      let errorMessage = [];
      if (errorResponse && typeof errorResponse === 'object') {
        errorMessage = Object.values(errorResponse).flat();
      } else {
        errorMessage = ['An error occurred while sending OTP'];
      }
      setError(errorMessage);
    }
    setLoading(false);
  };

  // Handle OTP Verification and then call Register API
  const handleVerifyOtp = async () => {
    setLoading(true);
    try {
      const otpResponse = await api.post('/otp/verifyOtp', { email: formData.email, otp });
      if (otpResponse.data.status) {
        // Call register API after OTP is verified
        const regResponse = await api.post('/auth/register', formData);
        if (regResponse.data.status) {
          toast.success('Registered! Wait for your approval.');
          navigate('/sign-in');
        } else {
          setError(['Registration failed']);
        }
      } else {
        setError(['Invalid OTP']);
      }
    } catch (err) {
      setError(['Error during OTP verification or registration']);
    }
    setLoading(false);
  };

  // OTP Timer Effect
  useEffect(() => {
    let timerInterval;
    if (showOtpModal && otpTimer > 0) {
      timerInterval = setInterval(() => {
        setOtpTimer((prev) => (prev > 0 ? prev - 1 : prev));
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [showOtpModal, otpTimer]);

  // Handle Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await api.post('/otp/sendOtp', { email: formData.email });
      toast.success('OTP Resent Successfully!');
      setOtpTimer(60);
    } catch (error) {
      toast.error('Failed to resend OTP');
    }
    setLoading(false);
  };

  return (
    <>
      <section className="auth bg-base d-flex flex-wrap">
        <div className="auth-left d-lg-block d-none">
          <div className="d-flex align-items-center flex-column h-100 justify-content-center">
            <img
              src="assets/images/auth/1.jpg"
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>
        <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
          <div className="max-w-464-px mx-auto w-100">
            <Link to="/" className="mb-40 max-w-290-px">
              {/* <img src="assets/images/logo.png" alt="" /> */}
              <h3>Brokerz</h3>
            </Link>
            <h6 className="mb-12">Sign Up to your Account</h6>
            {error && error.length > 0 && (
              <ul className="text-danger mb-16">
                {error.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            )}

            <form onSubmit={handleSignUp}>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="f7:person" />
                </span>
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  required
                />
              </div>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="mage:email" />
                </span>
                <input
                  type="email"
                  name="email"
                  placeholder="Business Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  required
                />
              </div>
              <div className="icon-field mb-16">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="mage:phone" />
                </span>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Mobile Phone"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  pattern="[0-9]{11}"
                  required
                />
              </div>
              <span className="text-sm text-secondary-light fst-italic">
                Password should contain at least one uppercase, one number, and one special character and minimum 8 characters (@$!%*?&).
              </span>
              <div className="icon-field mb-16 position-relative">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-control h-56-px bg-neutral-50 radius-12 pr-50"
                  required
                />
                <span
                  className="eye-icon position-absolute end-0 top-50 translate-middle-y me-3 cursor-pointer"
                  onClick={() => togglePasswordVisibility('password')}
                >
                  <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} />
                </span>
              </div>

              <div className="icon-field mb-16 position-relative">
                <span className="icon top-50 translate-middle-y">
                  <Icon icon="solar:lock-password-outline" />
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="password_confirmation"
                  placeholder="Confirm Password"
                  value={formData.password_confirmation}
                  onChange={handleInputChange}
                  className="form-control h-56-px bg-neutral-50 radius-12 pr-50"
                  required
                />
                <span
                  className="eye-icon position-absolute end-0 top-50 translate-middle-y me-3 cursor-pointer"
                  onClick={() => togglePasswordVisibility('password_confirmation')}
                >
                  <Icon icon={showConfirmPassword ? 'mdi:eye-off' : 'mdi:eye'} />
                </span>
              </div>

              <div className="mb-20">
                <label htmlFor="developer_id" className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Developer <span className="text-danger-600">*</span>
                </label>
                <Select
                  inputId="developer_id"
                  name="developer_id"
                  options={developerOptions}
                  value={developerOptions.find(option => option.value === formData.developer_id)}
                  onChange={handleSelectChange}
                  placeholder="Search or select a developer..."
                  isClearable
                  required
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
                      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
                      borderRadius: '0.5rem',
                      minHeight: '38px',
                    }),
                    input: (baseStyles) => ({ ...baseStyles, margin: '0px', padding: '0px' }),
                    valueContainer: (baseStyles) => ({ ...baseStyles, padding: '0px 8px' }),
                    placeholder: (baseStyles) => ({ ...baseStyles, color: '#6c757d' }),
                  }}
                />
              </div>
              <div className="icon-field mb-16">
                <span className="mt-12 text-sm text-secondary-light">
                  Upload your business card (PNG, JPG). <span className="text-sm text-secondary-light fst-italic">
                    max 2 mb.
                  </span>
                </span>
                <input
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={handleFileChange}
                  className="form-control h-56-px bg-neutral-50 radius-12"
                  required
                />


                <br></br>
              </div>
              <button
                type="submit"
                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Sign Up'}
              </button>
              <div className="mt-32 text-center text-sm">
                <p className="mb-0">
                  Already have an account?{" "}
                  <Link to="/sign-in" className="text-primary-600 fw-semibold">
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* OTP Modal using react-bootstrap */}
      <Modal
        show={showOtpModal}
        onHide={() => setShowOtpModal(false)}
        centered
        backdrop="static"
        className="transparent-modal" // Use this class to add custom transparent styles if desired
      >
        <Modal.Header closeButton>
          <Modal.Title>Verify your Email</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary-light text-sm mb-16">
            An OTP has been sent to your email. Enter it below:
          </p>
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className="form-control h-56-px bg-neutral-50 radius-12 mb-16"
          />
          <div className="d-flex justify-content-between align-items-center">
            {otpTimer > 0 ? (
              <span>Resend OTP in {otpTimer} seconds</span>
            ) : (
              <button onClick={handleResendOtp} className="btn btn-link" disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Resend OTP'}
              </button>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleVerifyOtp} variant="primary" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Verify OTP'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SignUpLayer;
